/**
 * Typed wrappers for the reusable toolkit-config custom endpoints (apply /
 * reapply / detach / list-applied-conferences). CRUD on the config records
 * themselves (list/get/create/update/delete under `/v1/superadmin/toolkits`)
 * goes through Refine's dataProvider.
 *
 * Backend reality (viq-backend/functions-superadmin):
 *  - Apply/detach hang off `/toolkits/{id}/conferences[/{cid}]`.
 *  - Reapply is the `:reapply` matrix-suffix route and returns `{ configId,
 *    count }` (NOT `{ updated }`).
 *  - list-applied-conferences returns `{ conferences }` of FULL Conference
 *    rows, so we unwrap to `Conference[]`.
 */
import type { Conference } from '@viq-admin/types';
import { superadminFetch } from './dataProvider';

/** POST /v1/superadmin/toolkits/{configId}/conferences — body `{ conferenceId }`. */
export async function applyConfig(
  configId: string,
  conferenceId: string,
): Promise<void> {
  await superadminFetch<void>(
    `/v1/superadmin/toolkits/${configId}/conferences`,
    { method: 'POST', body: JSON.stringify({ conferenceId }) },
  );
}

/**
 * POST /v1/superadmin/toolkits/{configId}/conferences:reapply — re-stamps the
 * config onto every conference already applied from it. Returns the count.
 */
export async function reapplyConfig(
  configId: string,
): Promise<{ configId: string; count: number }> {
  return superadminFetch<{ configId: string; count: number }>(
    `/v1/superadmin/toolkits/${configId}/conferences:reapply`,
    { method: 'POST' },
  );
}

/** DELETE /v1/superadmin/toolkits/{configId}/conferences/{conferenceId}. */
export async function detachConfig(
  configId: string,
  conferenceId: string,
): Promise<void> {
  await superadminFetch<void>(
    `/v1/superadmin/toolkits/${configId}/conferences/${conferenceId}`,
    { method: 'DELETE' },
  );
}

/** GET /v1/superadmin/toolkits/{configId}/conferences → conferences applied from this config. */
export async function listConfigConferences(
  configId: string,
): Promise<Conference[]> {
  const body = await superadminFetch<{ conferences: Conference[] }>(
    `/v1/superadmin/toolkits/${configId}/conferences`,
  );
  return body.conferences;
}
